import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Search, MessageSquare, Send, Phone, Mail } from 'lucide-react';

export default function Messages() {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api().get('/api/leads', { params: { limit: 200 } });
        setLeads(data.leads || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [api]);

  const selectLead = async (lead) => {
    setSelectedLead(lead);
    try {
      const { data } = await api().get(`/api/messages/${lead.id}`);
      setMessages(data);
    } catch { setMessages([]); }
  };

  const sendMessage = async () => {
    if (!msgBody.trim() || !selectedLead) return;
    setSending(true);
    try {
      await api().post('/api/messages/sms', { lead_id: selectedLead.id, body: msgBody, channel: 'SMS' });
      setMsgBody('');
      const { data } = await api().get(`/api/messages/${selectedLead.id}`);
      setMessages(data);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const filtered = leads.filter(l =>
    !search || `${l.first_name} ${l.last_name} ${l.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="messages-page" className="h-[calc(100vh-7rem)]">
      <div className="flex h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Contact List */}
        <div className="w-80 border-r border-slate-200 flex flex-col shrink-0">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Messages</h2>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="messages-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="Search contacts..."
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No contacts found</p>
            ) : (
              filtered.map(lead => (
                <button
                  key={lead.id}
                  data-testid={`msg-contact-${lead.id}`}
                  onClick={() => selectLead(lead)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    selectedLead?.id === lead.id ? 'bg-slate-50' : ''
                  }`}
                >
                  <p className="text-sm font-medium text-slate-900 truncate">{lead.first_name} {lead.last_name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone size={10} /> {lead.phone}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {!selectedLead ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto mb-3 text-slate-200" />
                <p className="text-sm">Select a contact to view messages</p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600">
                  {selectedLead.first_name?.[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{selectedLead.first_name} {selectedLead.last_name}</p>
                  <p className="text-xs text-slate-500">{selectedLead.phone}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No messages yet</p>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-lg px-4 py-2.5 text-sm ${
                        msg.direction === 'OUTBOUND' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'
                      }`}>
                        <p>{msg.body}</p>
                        <p className={`text-[10px] mt-1 ${msg.direction === 'OUTBOUND' ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(msg.sent_at).toLocaleTimeString()} {msg.channel === 'EMAIL' && <Mail size={8} className="inline" />}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-slate-200 flex gap-2">
                <input
                  data-testid="messages-input"
                  value={msgBody}
                  onChange={e => setMsgBody(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  placeholder="Type a message..."
                />
                <button
                  data-testid="messages-send"
                  onClick={sendMessage}
                  disabled={sending || !msgBody.trim()}
                  className="bg-slate-900 text-white px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-all disabled:opacity-50"
                >
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
